import "./createApiQueryClient-DwmsEdyU.js";
import "./utils/commonEnums/index.js";
import { useInfiniteQuery as e, useMutation as t, useQuery as n, useQueryClient as r } from "@tanstack/react-query";
import i from "lodash/fp/omit";
import { useCallback as a, useEffect as o, useMemo as s } from "react";
import { del as c, get as l, set as u } from "idb-keyval";
import { throttle as d } from "lodash/fp";
//#region src/utils/errorHandling.ts
var f = (e) => typeof e == "object" && !!e && "status" in e && "statusText" in e, p = (e) => !!e && typeof e == "object" && "error" in e, m = (e) => !!e && typeof e == "object" && "Fault" in e;
//#endregion
//#region src/utils/queryHooks.ts
function h(e, t = void 0, n = {}) {
	return _(e, t, n, async () => {
		let n = await e.request(t);
		if (n && typeof n == "object" && "error" in n && n?.error) {
			let e = await n.res.json().catch(() => void 0);
			return Promise.reject({
				statusText: n.res.statusText,
				status: n.res.status,
				response: e
			});
		}
		return n.data ?? null;
	});
}
var g = (e, t) => {
	let n = e.paramQueryKeyTransform ? e.paramQueryKeyTransform(t) : t;
	return n ? [n] : [];
};
function _(e, t = void 0, r = {}, i) {
	O(e);
	let { queryKey: a, ...o } = r, s = [
		...a || [],
		...e.queryKey,
		...g(e, t)
	];
	return n({
		...o,
		queryKey: s,
		queryFn: i
	});
}
var v = n, y = (e) => async (t, n) => {
	if (!t || !t?.length) return;
	let r = Array.isArray(t) ? t : [t];
	await Promise.all(r.map((t) => {
		let r = typeof t == "string" ? t : t.join(".");
		e.invalidateQueries({
			refetchType: n?.refetchType,
			predicate: (e) => e.queryKey.join(".").includes(r)
		});
	}));
}, b = () => {
	let e = r();
	return (t, n) => y(e)(t.invalidateQueries ?? [], n);
};
function x(e, t = {}) {
	return S(e, t, async (t) => {
		let n = await e.request(t);
		if (n && typeof n == "object" && "error" in n && n?.error) {
			let e = await n.res.json().catch(() => void 0);
			return Promise.reject({
				statusText: n.res.statusText,
				status: n.res.status,
				response: e
			});
		}
		return n.data;
	});
}
function S(e, n = {}, i) {
	let a = r();
	return O(e), t({
		...n,
		mutationFn: i,
		onSuccess: (t, r, i, o) => {
			if (n.onSuccess && n.onSuccess(t, r, i, o), n.invalidateQueries) {
				let t = n.invalidateQueries(e?.invalidateQueries || []);
				y(a)(t);
			} else y(a)(e.invalidateQueries);
		}
	});
}
function C(t, n, r) {
	O(t);
	let { queryKey: i, ...a } = r, o = [
		...i || [],
		...t.queryKey,
		...g(t, n),
		"infinite"
	];
	return e({
		...a,
		queryKey: o,
		queryFn: async (e) => {
			let r = await t.request({
				...e.pageParam ?? {},
				...n
			});
			if (r && typeof r == "object" && "error" in r && r?.error) throw Error("Could not fetch");
			return r.data;
		}
	});
}
async function w(e, t, n = void 0, r) {
	O(t);
	let i = [...t.queryKey, ...g(t, n)];
	return e.fetchQuery({
		queryKey: i,
		queryFn: async () => {
			let e = await t.request(n);
			return e && typeof e == "object" && "error" in e && e?.error ? Promise.reject(e) : e.data ?? null;
		},
		staleTime: r?.staleTime
	});
}
async function T(e, t, n = void 0, r) {
	O(t);
	let i = [...t.queryKey, ...g(t, n)];
	return e.ensureQueryData({
		queryKey: i,
		queryFn: async () => {
			let e = await t.request(n);
			return e && typeof e == "object" && "error" in e && e?.error ? Promise.reject(e) : e.data ?? null;
		},
		staleTime: r?.staleTime
	});
}
var E = r, D = (e) => "request" in e, O = (e) => {
	if (!D(e)) throw Error("Incompatible query function provided, please use sequence API function");
}, k = (e) => new Promise((t) => setTimeout(t, e)), A = (e, t) => Math.floor(Math.random() * (t - e + 1)) + e;
function j(e, t = void 0, n = {}, r = {}) {
	return _(e, t, n, async () => (await k(r.delay ?? A(250, 3e3)), r.throwError ? Promise.reject({
		statusText: "Mock error thrown",
		status: 500,
		response: {}
	}) : r.mock ?? e.mocking.mock));
}
function M(e, t = {}, n = {}) {
	return S(e, t, async (t) => (await k(n.delay ?? A(250, 3e3)), n.throwError ? Promise.reject({
		statusText: "Mock error thrown",
		status: 500,
		response: {}
	}) : n.mock ?? e.mocking.mock));
}
//#endregion
//#region src/utils/useSequenceInfiniteQuery.ts
var N = 100, P = (e, t, n) => {
	if (!e || t.length >= n) return null;
	let { pagination: r, items: i } = e;
	return !r.after || !i.length ? null : {
		before: r.before,
		after: r.after
	};
}, F = Object.assign((e, t = {}, n = {}) => {
	let r = n.maxPages ?? N;
	return { infiniteQuery: C(s(() => ({
		...e,
		request: async (t, n) => {
			let r = await e.request(t, n);
			return !r?.data || r.data.pagination ? r : {
				...r,
				data: {
					...r.data,
					pagination: {
						before: "",
						after: "",
						totalResultSize: r.data.items.length
					}
				}
			};
		}
	}), [e]), t ?? {}, {
		staleTime: 1e3 * 5,
		initialPageParam: {
			before: void 0,
			after: void 0
		},
		getNextPageParam: (e, t) => P(e, t, r),
		...i("maxPages")(n)
	}) };
}, { MAX_PAGES: N }), I = 50, L = (e) => async (t) => {
	let n = [], r = (await e({
		limit: 100,
		...t
	}))?.data;
	r?.items?.forEach((e) => n.push(e));
	let i = 1;
	for (; r && r.items.length > 0 && r.pagination.after && i < I;) r = (await e({
		limit: 100,
		after: r.pagination.after,
		...t
	}))?.data, r?.items?.forEach((e) => n.push(e)), i++;
	return n;
}, R = (e) => a(L(e), [e]), z = "reactQuery", B = (e) => e.map((e) => e.queryKey.map((t) => `[${e.queryKey.map((e) => JSON.stringify(e)).join(", ")}]`).join("\n")).join(", "), V = (e) => {
	try {
		return structuredClone(e), !0;
	} catch {
		return !1;
	}
};
function H(e = z) {
	return {
		persistClient: d(5e3)(async (t) => {
			try {
				await u(e, {
					...t,
					clientState: {
						...t.clientState,
						queries: t.clientState.queries.filter(V)
					}
				});
			} catch {
				console.error(`Could not persist client to indexed DB with queries: ${B(t.clientState.queries)}`);
				let e = t.clientState.queries.filter((e) => !V(e));
				console.error(`The following queries could not be cloned: ${B(e)} `);
			}
		}),
		restoreClient: async () => await l(e),
		removeClient: async () => {
			await c(e);
		}
	};
}
//#endregion
//#region src/utils/useLoadAllItemsQuery.ts
var U = (e, t = {}, n = {}) => {
	let { infiniteQuery: r } = F(e, t, {
		...n,
		staleTime: n.staleTime ?? 1e3 * 5
	}), i = r.hasNextPage && !r.isPending && !r.isError;
	return o(() => {
		i && r.fetchNextPage();
	}, [
		r.fetchNextPage,
		i,
		r.data?.pages.length
	]), r;
};
//#endregion
//#region src/utils/extractValidationErrors.ts
function W(e) {
	return !f(e) || !p(e.response) || e.response.error.type !== "validationError" ? [] : e.response.error.errors.filter((e) => e.code === "validationError").map((e) => e.message);
}
//#endregion
export { b as _, F as a, p as b, S as c, w as d, E as f, h as g, x as h, R as i, _ as l, C as m, U as n, M as o, v as p, H as r, j as s, W as t, T as u, m as v, f as y };

//# sourceMappingURL=utils-C8-2hwbF.js.map